import React, { useState, useEffect, useContext } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { AppContext } from '../../App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import AdminLayout from '../../components/AdminLayout';
import {
  FaPlus, FaSave, FaTrash, FaEdit, FaArrowLeft, FaGripVertical,
  FaChevronDown, FaChevronRight, FaEye, FaEyeSlash
} from 'react-icons/fa';
import {
  Ruler, Cog, Gauge, Wrench, Shield, Zap, Wifi, Thermometer, Award,
  Link as LinkIcon, Layers, Truck, Scale, FileCheck, MapPin, Star,
  GraduationCap, Activity, Box
} from 'lucide-react';

const SECTION_ICONS = {
  Ruler, Cog, Gauge, Wrench, Shield, Zap, Wifi, Thermometer, Award,
  Link: LinkIcon, Layers, Truck, Scale, FileCheck, MapPin, Star,
  GraduationCap, Activity, Box
};

const FIELD_TYPES = [
  { value: 'text', label: 'Texte' },
  { value: 'number', label: 'Nombre' },
  { value: 'select', label: 'Liste déroulante' },
  { value: 'multiselect', label: 'Multi-sélection' },
  { value: 'boolean', label: 'Oui/Non' },
  { value: 'date', label: 'Date' },
  { value: 'textarea', label: 'Texte long' },
];

export default function TemplateFields() {
  const { templateId } = useParams();
  const { i18n } = useTranslation();
  const { API } = useContext(AppContext);
  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedSections, setExpandedSections] = useState({});
  
  // Section dialog
  const [sectionDialogOpen, setSectionDialogOpen] = useState(false);
  const [editingSection, setEditingSection] = useState(null);
  const [sectionForm, setSectionForm] = useState({
    name_fr: '', name_en: '', icon_lucide: 'Box', description_fr: '', description_en: '',
    position: 0, collapsible: true, collapsed_default: false, visible: true
  });
  
  // Field dialog
  const [fieldDialogOpen, setFieldDialogOpen] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [fieldForm, setFieldForm] = useState({
    name: '', label_fr: '', label_en: '', field_type: 'text', unit: '',
    default_value: '', options: [], required: false, visible: true,
    help_text_fr: '', help_text_en: '', placeholder: '', section_id: '',
    position: 0, comparator_enabled: false, comparator_direction: 'asc'
  });
  const [optionsText, setOptionsText] = useState('');

  useEffect(() => {
    fetchTemplate();
  }, [templateId]);

  const fetchTemplate = async () => {
    try {
      const res = await axios.get(`${API}/templates/${templateId}`);
      setTemplate(res.data);
      
      // Expand all sections
      const expanded = {};
      (res.data.sections || []).forEach(s => { expanded[s.id] = true; });
      setExpandedSections(expanded);
    } catch (error) {
      console.error('Error fetching template:', error);
      toast.error('Template non trouvé');
    } finally {
      setLoading(false);
    }
  };

  // ─── SECTION HANDLERS ────────────────────────────────────────────────
  const openSectionDialog = (section = null) => {
    if (section) {
      setEditingSection(section);
      setSectionForm({
        name_fr: section.name_fr || '',
        name_en: section.name_en || '',
        icon_lucide: section.icon_lucide || 'Box',
        description_fr: section.description_fr || '',
        description_en: section.description_en || '',
        position: section.position || 0,
        collapsible: section.collapsible !== false,
        collapsed_default: section.collapsed_default || false,
        visible: section.visible !== false,
      });
    } else {
      setEditingSection(null);
      setSectionForm({
        name_fr: '', name_en: '', icon_lucide: 'Box', description_fr: '', description_en: '',
        position: (template?.sections?.length || 0) + 1, collapsible: true, collapsed_default: false, visible: true
      });
    }
    setSectionDialogOpen(true);
  };

  const handleSaveSection = async () => {
    try {
      const token = localStorage.getItem('token');
      
      if (editingSection) {
        await axios.put(`${API}/templates/${templateId}/sections/${editingSection.id}`, sectionForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Section mise à jour');
      } else {
        await axios.post(`${API}/templates/${templateId}/sections`, sectionForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Section créée');
      }
      
      setSectionDialogOpen(false);
      fetchTemplate();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const handleDeleteSection = async (sectionId) => {
    const fieldsInSection = (template?.fields || []).filter(f => f.section_id === sectionId);
    const message = fieldsInSection.length > 0 
      ? `Cette section contient ${fieldsInSection.length} champs qui seront aussi supprimés. Continuer ?`
      : 'Supprimer cette section ?';
    
    if (!window.confirm(message)) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/templates/${templateId}/sections/${sectionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Section supprimée');
      fetchTemplate();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  // ─── FIELD HANDLERS ──────────────────────────────────────────────────
  const openFieldDialog = (field = null, sectionId = null) => {
    if (field) {
      setEditingField(field);
      setFieldForm({
        name: field.name || '',
        label_fr: field.label_fr || '',
        label_en: field.label_en || '',
        field_type: field.field_type || 'text',
        unit: field.unit || '',
        default_value: field.default_value || '',
        options: field.options || [],
        required: field.required || false,
        visible: field.visible !== false,
        help_text_fr: field.help_text_fr || '',
        help_text_en: field.help_text_en || '',
        placeholder: field.placeholder || '',
        section_id: field.section_id || '',
        position: field.position || 0,
        comparator_enabled: field.comparator_enabled || false,
        comparator_direction: field.comparator_direction || 'asc',
      });
      setOptionsText((field.options || []).join('\n'));
    } else {
      setEditingField(null);
      const sectionFields = (template?.fields || []).filter(f => f.section_id === sectionId);
      setFieldForm({
        name: '', label_fr: '', label_en: '', field_type: 'text', unit: '',
        default_value: '', options: [], required: false, visible: true,
        help_text_fr: '', help_text_en: '', placeholder: '', section_id: sectionId || '',
        position: sectionFields.length + 1, comparator_enabled: false, comparator_direction: 'asc'
      });
      setOptionsText('');
    }
    setFieldDialogOpen(true);
  };

  const handleSaveField = async () => {
    const options = optionsText.split('\n').map(o => o.trim()).filter(o => o);
    const dataToSave = { ...fieldForm, options };
    
    try {
      const token = localStorage.getItem('token');
      
      if (editingField) {
        await axios.put(`${API}/templates/${templateId}/fields/${editingField.id}`, dataToSave, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Champ mis à jour');
      } else {
        await axios.post(`${API}/templates/${templateId}/fields`, dataToSave, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Champ créé');
      }
      
      setFieldDialogOpen(false);
      fetchTemplate();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const handleDeleteField = async (fieldId) => {
    if (!window.confirm('Supprimer ce champ ?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/templates/${templateId}/fields/${fieldId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Champ supprimé');
      fetchTemplate();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({ ...prev, [sectionId]: !prev[sectionId] }));
  };

  const getFieldsForSection = (sectionId) => {
    return (template?.fields || [])
      .filter(f => f.section_id === sectionId)
      .sort((a, b) => (a.position || 0) - (b.position || 0));
  };

  const getFieldTypeLabel = (type) => {
    const found = FIELD_TYPES.find(t => t.value === type);
    return found ? found.label : type;
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="p-12 text-center">
          <div className="w-8 h-8 border-4 border-[#635BFF] border-t-transparent rounded-full animate-spin mx-auto"></div>
        </div>
      </AdminLayout>
    );
  }

  if (!template) {
    return (
      <AdminLayout>
        <div className="p-12 text-center text-gray-500">Template non trouvé</div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="min-h-screen bg-[#F5F5F5]" data-testid="template-fields">
        <div className="p-4 md:p-6">
          {/* Header */}
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
            <div className="flex items-center gap-4">
              <Link to="/admin/templates">
                <Button variant="outline" size="sm">
                  <FaArrowLeft className="mr-2" /> Retour
                </Button>
              </Link>
              <div>
                <h1 className="text-2xl font-bold text-[#0A2540]">
                  {template.name_fr}
                </h1>
                <p className="text-gray-600 text-sm">
                  {template.sections?.length || 0} sections • {template.fields?.length || 0} champs
                </p>
              </div>
            </div>
            
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => openSectionDialog()}>
                <FaPlus className="mr-2" /> Ajouter section
              </Button>
            </div>
          </div>

          {/* Sections & Fields */}
          <div className="space-y-4">
            {(template.sections || [])
              .sort((a, b) => (a.position || 0) - (b.position || 0))
              .map((section) => {
                const IconComp = SECTION_ICONS[section.icon_lucide] || Box;
                const fields = getFieldsForSection(section.id);
                
                return (
                  <Card key={section.id} className={!section.visible ? 'opacity-60' : ''}>
                    {/* Section Header */}
                    <div className="flex items-center justify-between p-4 bg-gray-50 border-b">
                      <button
                        onClick={() => toggleSection(section.id)}
                        className="flex items-center gap-3"
                      >
                        {expandedSections[section.id] ? (
                          <FaChevronDown className="text-gray-400" />
                        ) : (
                          <FaChevronRight className="text-gray-400" />
                        )}
                        <div className="w-10 h-10 bg-[#635BFF]/10 rounded-lg flex items-center justify-center">
                          <IconComp size={20} className="text-[#635BFF]" />
                        </div>
                        <div className="text-left">
                          <h3 className="font-semibold text-[#0A2540]">{section.name_fr}</h3>
                          <span className="text-sm text-gray-500">{fields.length} champs</span>
                        </div>
                      </button>
                      <div className="flex items-center gap-2">
                        {!section.visible && (
                          <span className="text-xs bg-gray-200 px-2 py-1 rounded-full">Masquée</span>
                        )}
                        <Button size="sm" variant="ghost" onClick={() => openSectionDialog(section)}>
                          <FaEdit />
                        </Button>
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          className="text-red-500 hover:bg-red-50"
                          onClick={() => handleDeleteSection(section.id)}
                        >
                          <FaTrash />
                        </Button>
                      </div>
                    </div>

                    {/* Fields List */}
                    {expandedSections[section.id] && (
                      <CardContent className="p-0">
                        <div className="divide-y">
                          {fields.map((field) => (
                            <div
                              key={field.id}
                              className={`p-3 flex items-center gap-3 hover:bg-gray-50 ${!field.visible ? 'opacity-50' : ''}`}
                            >
                              <FaGripVertical className="text-gray-300 cursor-move" />
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <span className="font-medium text-[#0A2540]">{field.label_fr}</span>
                                  {field.required && <span className="text-red-500 text-xs">*</span>}
                                  {field.unit && (
                                    <span className="text-xs bg-gray-100 px-2 py-0.5 rounded">{field.unit}</span>
                                  )}
                                </div>
                                <div className="flex items-center gap-2 text-xs text-gray-500">
                                  <span>{getFieldTypeLabel(field.field_type)}</span>
                                  {field.comparator_enabled && (
                                    <span className="bg-cyan-100 text-cyan-700 px-1.5 py-0.5 rounded">Comparateur</span>
                                  )}
                                </div>
                              </div>
                              <div className="flex items-center gap-1">
                                {field.visible ? (
                                  <FaEye className="text-gray-400" size={12} />
                                ) : (
                                  <FaEyeSlash className="text-gray-300" size={12} />
                                )}
                                <Button size="sm" variant="ghost" onClick={() => openFieldDialog(field)}>
                                  <FaEdit size={12} />
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="ghost" 
                                  className="text-red-500 hover:bg-red-50"
                                  onClick={() => handleDeleteField(field.id)}
                                >
                                  <FaTrash size={12} />
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                        <div className="p-3 border-t">
                          <Button 
                            size="sm" 
                            variant="outline" 
                            className="w-full"
                            onClick={() => openFieldDialog(null, section.id)}
                          >
                            <FaPlus className="mr-2" /> Ajouter un champ
                          </Button>
                        </div>
                      </CardContent>
                    )}
                  </Card>
                );
              })}

            {(template.sections || []).length === 0 && (
              <Card className="p-12 text-center text-gray-500">
                <p className="mb-4">Aucune section. Commencez par en créer une.</p>
                <Button onClick={() => openSectionDialog()}>
                  <FaPlus className="mr-2" /> Créer une section
                </Button>
              </Card>
            )}
          </div>
        </div>
      </div>

      {/* Section Dialog */}
      <Dialog open={sectionDialogOpen} onOpenChange={setSectionDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingSection ? 'Modifier la section' : 'Nouvelle section'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Nom (FR) *</Label>
                <Input value={sectionForm.name_fr} onChange={(e) => setSectionForm({...sectionForm, name_fr: e.target.value})} />
              </div>
              <div>
                <Label>Name (EN)</Label>
                <Input value={sectionForm.name_en} onChange={(e) => setSectionForm({...sectionForm, name_en: e.target.value})} />
              </div>
            </div>
            
            <div>
              <Label>Icône</Label>
              <div className="flex flex-wrap gap-2 p-3 bg-gray-50 rounded-lg mt-1">
                {Object.keys(SECTION_ICONS).map(iconName => {
                  const IconComp = SECTION_ICONS[iconName];
                  return (
                    <button
                      key={iconName}
                      type="button"
                      onClick={() => setSectionForm({...sectionForm, icon_lucide: iconName})}
                      className={`w-10 h-10 rounded-lg flex items-center justify-center hover:bg-gray-200 ${
                        sectionForm.icon_lucide === iconName ? 'bg-[#635BFF]/20 ring-2 ring-[#635BFF]' : ''
                      }`}
                    >
                      <IconComp size={20} className="text-[#635BFF]" />
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex items-center gap-2">
                <Switch checked={sectionForm.visible} onCheckedChange={(v) => setSectionForm({...sectionForm, visible: v})} />
                <Label>Visible</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={sectionForm.collapsible} onCheckedChange={(v) => setSectionForm({...sectionForm, collapsible: v})} />
                <Label>Repliable</Label>
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <Button variant="outline" onClick={() => setSectionDialogOpen(false)}>Annuler</Button>
              <Button className="bg-[#635BFF]" onClick={handleSaveSection}>
                <FaSave className="mr-2" /> Enregistrer
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Field Dialog */}
      <Dialog open={fieldDialogOpen} onOpenChange={setFieldDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingField ? 'Modifier le champ' : 'Nouveau champ'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Nom technique (key) *</Label>
                <Input value={fieldForm.name} onChange={(e) => setFieldForm({...fieldForm, name: e.target.value})} placeholder="ex: max_depth" />
              </div>
              <div>
                <Label>Type de champ *</Label>
                <select
                  value={fieldForm.field_type}
                  onChange={(e) => setFieldForm({...fieldForm, field_type: e.target.value})}
                  className="w-full h-10 border border-gray-200 rounded-md px-3"
                >
                  {FIELD_TYPES.map(t => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Label (FR) *</Label>
                <Input value={fieldForm.label_fr} onChange={(e) => setFieldForm({...fieldForm, label_fr: e.target.value})} />
              </div>
              <div>
                <Label>Label (EN)</Label>
                <Input value={fieldForm.label_en} onChange={(e) => setFieldForm({...fieldForm, label_en: e.target.value})} />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Unité</Label>
                <Input value={fieldForm.unit} onChange={(e) => setFieldForm({...fieldForm, unit: e.target.value})} placeholder="kg, m, kW..." />
              </div>
              <div>
                <Label>Valeur par défaut</Label>
                <Input value={fieldForm.default_value} onChange={(e) => setFieldForm({...fieldForm, default_value: e.target.value})} />
              </div>
              <div>
                <Label>Placeholder</Label>
                <Input value={fieldForm.placeholder} onChange={(e) => setFieldForm({...fieldForm, placeholder: e.target.value})} />
              </div>
            </div>

            {(fieldForm.field_type === 'select' || fieldForm.field_type === 'multiselect') && (
              <div>
                <Label>Options (une par ligne)</Label>
                <textarea
                  value={optionsText}
                  onChange={(e) => setOptionsText(e.target.value)}
                  className="w-full h-24 border border-gray-200 rounded-md px-3 py-2"
                  placeholder="Option 1&#10;Option 2&#10;Option 3"
                />
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Aide (FR)</Label>
                <Input value={fieldForm.help_text_fr} onChange={(e) => setFieldForm({...fieldForm, help_text_fr: e.target.value})} />
              </div>
              <div>
                <Label>Help (EN)</Label>
                <Input value={fieldForm.help_text_en} onChange={(e) => setFieldForm({...fieldForm, help_text_en: e.target.value})} />
              </div>
            </div>

            <div className="p-4 bg-gray-50 rounded-lg space-y-3">
              <div className="flex items-center gap-6">
                <div className="flex items-center gap-2">
                  <Switch checked={fieldForm.required} onCheckedChange={(v) => setFieldForm({...fieldForm, required: v})} />
                  <Label>Obligatoire</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch checked={fieldForm.visible} onCheckedChange={(v) => setFieldForm({...fieldForm, visible: v})} />
                  <Label>Visible</Label>
                </div>
              </div>
              
              <div className="pt-2 border-t">
                <div className="flex items-center gap-2 mb-2">
                  <Switch checked={fieldForm.comparator_enabled} onCheckedChange={(v) => setFieldForm({...fieldForm, comparator_enabled: v})} />
                  <Label>Activer dans le comparateur</Label>
                </div>
                {fieldForm.comparator_enabled && (
                  <div className="flex items-center gap-4 ml-6">
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="radio"
                        value="asc"
                        checked={fieldForm.comparator_direction === 'asc'}
                        onChange={() => setFieldForm({...fieldForm, comparator_direction: 'asc'})}
                      />
                      Plus élevé = meilleur
                    </label>
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="radio"
                        value="desc"
                        checked={fieldForm.comparator_direction === 'desc'}
                        onChange={() => setFieldForm({...fieldForm, comparator_direction: 'desc'})}
                      />
                      Plus bas = meilleur
                    </label>
                  </div>
                )}
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <Button variant="outline" onClick={() => setFieldDialogOpen(false)}>Annuler</Button>
              <Button className="bg-[#635BFF]" onClick={handleSaveField}>
                <FaSave className="mr-2" /> Enregistrer
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
